import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import StatCard from './StatCard';
import StatCardSkeleton from './skeletons/StatCardSkeleton';
import { orderPageCardsByIds, PageStatCard, type StatCardId } from './utils/buildPageCards';

interface DashboardStatCardsGridProps {
  loading: boolean;
  hasTableCounts: boolean;
  pageCards: PageStatCard[];
  order: StatCardId[];
  onReorder: (order: StatCardId[]) => void;
}

function SortableStatCard({ card }: { card: PageStatCard }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.92 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  const { id: _id, ...statProps } = card;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative h-full"
    >
      <div className="relative flex h-full min-h-0 overflow-hidden rounded-2xl shadow-md ring-1 ring-slate-200/70 transition-shadow hover:shadow-lg dark:ring-slate-600/80">
        <button
          type="button"
          aria-label="Kartı sürükleyerek yerini değiştir"
          className="flex w-12 shrink-0 cursor-grab touch-none flex-col items-center justify-center border-0 bg-slate-100/95 text-slate-500 hover:bg-slate-200/95 active:cursor-grabbing dark:bg-slate-800/95 dark:text-slate-400 dark:hover:bg-slate-700/95"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5 shrink-0" aria-hidden />
        </button>
        <Link to={statProps.link} className="min-w-0 flex-1">
          <StatCard
            {...statProps}
            className="rounded-l-none border-l-0 shadow-none ring-0"
          />
        </Link>
      </div>
    </div>
  );
}

export const DashboardStatCardsGrid: React.FC<DashboardStatCardsGridProps> = ({
  loading,
  hasTableCounts,
  pageCards,
  order,
  onReorder,
}) => {
  const orderedCards = useMemo(
    () => orderPageCardsByIds(pageCards, order),
    [pageCards, order]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id as StatCardId);
    const newIndex = order.indexOf(over.id as StatCardId);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(order, oldIndex, newIndex));
  };

  if (loading && !hasTableCounts) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <StatCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={order} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {orderedCards.map(card => (
            <SortableStatCard key={card.id} card={card} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
